import { useState, useEffect } from 'react';

/**
 * A persisted record: ONE localStorage key, owned by one domain.
 *
 * This replaces `useLocalStorage` (thd-storage-utils) for the assistant's session records.
 * That hook's value is a per-DOCUMENT React mirror of the key rather than the key itself, and
 * every consumer here ended up compensating for the gap — five keep-a-ref-in-sync effects, a
 * bypass reader for cross-tab truth, a bridge from the browser's `storage` event onto the
 * hook's private announcement bus, and a repair pass for identities that diverged because two
 * documents each held their own mirror. All of it collapses once `get()` IS the record.
 *
 * What that hook did that mattered, and is kept here:
 *   - The wire format, byte for byte: `encodeURIComponent(JSON.stringify({ value, expiry }))`
 *     with `(`/`)` escaped. NOT a security control — it is a lossless round-trip, so it
 *     filters nothing, and the read path never validated shape. It is kept because the format
 *     is shared: `magic-apron-legacy` (a declared dependency) reads `maa-session` through
 *     thd-storage-utils, so a legacy build co-existing on the host page must still parse ours.
 *   - Expiry: an envelope past its date reads as absent and is deleted. An envelope with no
 *     expiry never ages out, matching `new Date(undefined) <= Date.now()` being false there.
 *   - Fail-soft reads: unparseable storage reads as absent instead of throwing.
 *
 * What is deliberately different:
 *   - `get()` reads storage, so it is the SYNCHRONOUS authority. Callers no longer need a ref.
 *   - `update()` merges onto that current value, so a lost update is not possible. The old
 *     setter took a whole object, which is why every writer spread a render-scoped snapshot
 *     and why two writes in a tick clobbered each other.
 *   - Subscribers are the record's own, so a write notifies every holder in this document
 *     directly rather than through a global event bus.
 *   - `crossTab` opts a record into peer-tab notification. Off by default: adopting a peer's
 *     value mid-page is wrong for identity (it splits the analytics trail) and for the live
 *     agent (its own BroadcastChannel protocol owns that), and right only for UI prefs.
 */

// Matches thd-storage-utils' encodeStorage: encodeURIComponent leaves `!'()*` alone.
const encode = (raw) => encodeURIComponent(raw).replace(/\(/g, '%28').replace(/\)/g, '%29');

const ONE_YEAR_MS = 365 * 24 * 60 * 60 * 1000;

const hasStorage = () => typeof window !== 'undefined' && !!window.localStorage;

/**
 * Read one key's value, or null when it is absent, unparseable, or past its expiry.
 * An expired envelope is removed, the way thd-storage-utils' reader does.
 */
export const readRecord = (key) => {
  if (!hasStorage()) {
    return null;
  }
  let envelope;
  try {
    const raw = window.localStorage.getItem(key);
    // decodeURIComponent is a no-op on a plain JSON string, so an unencoded envelope (what
    // tests and older writers produce) reads back fine.
    envelope = raw === null ? null : JSON.parse(decodeURIComponent(raw));
  } catch {
    return null;
  }
  if (envelope === null || typeof envelope !== 'object') {
    return null;
  }
  // No expiry means it never ages out; `new Date(undefined)` is an Invalid Date and every
  // comparison against it is false, which is the behaviour records written so far rely on.
  if (new Date(envelope.expiry) <= Date.now()) {
    try {
      window.localStorage.removeItem(key);
    } catch { /* storage unavailable — nothing to remove */ }
    return null;
  }
  return envelope.value ?? null;
};

/** Write one key's value in the shared envelope format. Never throws. */
export const writeRecord = (key, value) => {
  if (!hasStorage()) {
    return;
  }
  try {
    window.localStorage.setItem(key, encode(JSON.stringify({
      value,
      expiry: new Date(Date.now() + ONE_YEAR_MS),
    })));
  } catch { /* quota or private mode — the in-memory value still stands for this page */ }
};

// Records that asked for peer-tab notification, by key. One `storage` listener serves them
// all; the browser only fires it in OTHER documents, so a write here never re-enters.
const crossTabRecords = new Map();
let storageListener = null;

const ensureStorageListener = () => {
  if (storageListener || typeof window === 'undefined' || !window.addEventListener) {
    return;
  }
  storageListener = ({ key, storageArea }) => {
    if (storageArea && storageArea !== window.localStorage) {
      return;
    }
    // A null key means the peer cleared storage wholesale, which changes every record.
    crossTabRecords.forEach((record, recordKey) => {
      if (key === null || key === recordKey) {
        record.notify();
      }
    });
  };
  window.addEventListener('storage', storageListener);
};

/**
 * @param {Object} options
 * @param {string} options.key - the localStorage key this record owns.
 * @param {() => Object} options.buildDefault - value to use when the key is absent. Resolved
 *   at most ONCE per document and reused, so every holder here agrees on it even when the
 *   record was never persisted (two holders each minting their own would be the bug).
 * @param {boolean} [options.crossTab=false] - notify subscribers when a PEER tab writes.
 * @returns {{ key: string, get: Function, set: Function, update: Function,
 *   subscribe: Function, notify: Function, seed: Function }}
 */
export const createRecord = ({ key, buildDefault, crossTab = false }) => {
  const listeners = new Set();
  let resolvedDefault = null;

  const notify = () => listeners.forEach((listener) => listener());

  const getDefault = () => {
    if (!resolvedDefault) {
      resolvedDefault = buildDefault();
    }
    return resolvedDefault;
  };

  /** The record's current value: what is persisted, else this document's resolved default. */
  const get = () => readRecord(key) ?? getDefault();

  const set = (next) => {
    writeRecord(key, next);
    notify();
    return next;
  };

  /**
   * Merge onto the CURRENT persisted value. This is the whole point of the store: a writer
   * cannot revert a field it did not touch, whether the other write came from a sibling hook
   * a moment ago or from a timer holding a snapshot taken seconds earlier.
   * @param {Object|Function} patch - fields to merge, or `(current) => fields`.
   */
  const update = (patch) => {
    const current = get();
    return set({ ...current, ...(typeof patch === 'function' ? patch(current) : patch) });
  };

  /** Persist the default if the key is absent, so peers read this value instead of minting. */
  const seed = () => {
    const persisted = readRecord(key);
    if (persisted) {
      return persisted;
    }
    return set(getDefault());
  };

  /**
   * Forget this record: drop the stored key AND the resolved default, so the next read
   * resolves from scratch. The default is memoized for the life of the document, which is
   * what makes every holder agree on it, so clearing the key alone would not be enough.
   *
   * PRECONDITION: no live subscribers. Clearing notifies, and a subscriber re-reads on
   * notification — at which point storage is empty and the memo is null, so `get()` resolves a
   * NEW default and caches it. Since every default here is derived (drawer prefs and live
   * agent from the legacy `maa-session` envelope, identity from the `thda.s` cookie), that
   * replacement samples whatever the world looks like mid-teardown and pins it for the rest of
   * the document. The clear appears to work and then silently half-undoes itself.
   *
   * That is a caller error rather than a state this can recover from — the two halves of
   * `clear()` (invalidate document state, tell subscribers) genuinely fight each other — so it
   * is enforced rather than documented. It throws BEFORE mutating, leaving the record intact.
   */
  const clear = () => {
    if (listeners.size > 0) {
      throw new Error(
        `Cannot clear the "${key}" record while ${listeners.size} subscriber(s) are live: `
        + 'the clear notifies, the subscriber re-reads, and a fresh default gets cached from '
        + 'the storage just emptied. Unmount (React Testing Library `cleanup()`) or '
        + 'unsubscribe first, then clear.'
      );
    }
    if (hasStorage()) {
      try {
        window.localStorage.removeItem(key);
      } catch { /* storage unavailable — nothing to remove */ }
    }
    resolvedDefault = null;
    notify();
  };

  const subscribe = (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };

  const record = {
    key, get, set, update, seed, clear, subscribe, notify,
  };
  if (crossTab) {
    crossTabRecords.set(key, record);
    ensureStorageListener();
  }
  return record;
};

/**
 * Subscribe a component to a record. Returns `[value, update]` — `update` merges, so call
 * sites pass only the fields they own.
 *
 * The value is a render-time snapshot like any React state, but unlike the old hook it is
 * never the authority: anything needing the value at write time calls `record.get()`, which
 * reads storage. Seeds on mount, matching what thd-storage-utils did with a default.
 *
 * React here is 16, so there is no `useSyncExternalStore` — and no concurrent rendering
 * either, so a subscribe-in-effect cannot tear.
 */
export const useRecord = (record) => {
  const [value, setValue] = useState(record.get);

  useEffect(() => {
    record.seed();
    const unsubscribe = record.subscribe(() => setValue(record.get()));
    // A peer write (or a sibling's) can land between the first render and this effect.
    setValue(record.get());
    return unsubscribe;
  }, [record]);

  return [value, record.update];
};
